import { Link } from 'react-router-dom';
import {
  Palette, FlaskConical, Images, ListChecks, History, Home, FileText,
  ArrowRight, ShieldCheck, Info, HelpCircle,
} from 'lucide-react';
import Card from '@/components/ui/Card';
import { useAuthStore } from '@/stores/auth-store';
import { isDesigner } from '@/lib/roles';
import { SITE_NAME } from '@/config/site';

/**
 * Where a designer lands, and the answer to "what am I allowed to touch?"
 *
 * An outside contractor arriving in an admin panel with two visible sections
 * and eighteen invisible ones will reasonably assume something is broken or
 * that they are missing a permission. This says out loud what the account can
 * do, what it deliberately cannot, and where the four tools live — so the first
 * message is not "I think my login is wrong".
 *
 * Also the honest place to state the sandbox/live distinction, which is the one
 * thing that costs real money to get wrong.
 */
export default function DesignerStart() {
  const user = useAuthStore((s) => s.user);
  const designer = isDesigner(user?.role);

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {user?.firstName ? `Welcome, ${user.firstName}` : 'Welcome'}
        </h1>
        <p className="mt-1 text-gray-600">
          You're set up to work on the design of {SITE_NAME}. Here's where everything is.
        </p>
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        <StartCard
          to="/admin/settings/design-checklist"
          icon={ListChecks}
          title="Start here — the checklist"
          body="Every image this site needs, at what size and format, and whether it's there yet. Copy the spec sheet and work from it offline."
        />
        <StartCard
          to="/admin/settings/sandbox"
          icon={FlaskConical}
          title="Design Sandbox"
          body="Try a whole look without touching the live site. Preview it against real pages, compare it to what's live, and publish when it's right."
        />
        <StartCard
          to="/admin/media"
          icon={Images}
          title="Media Library"
          body="Everything already uploaded, plus stock search and AI generation in one picker. Shows where each image is used before you replace it."
        />
        <StartCard
          to="/admin/settings/theme"
          icon={Palette}
          title="Theme"
          body="Colours and fonts for the live site. For anything you're still deciding on, work in the sandbox instead."
        />
      </div>

      <Card padding="lg" className="mb-6">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
          <Info className="h-5 w-5 text-sage-600" aria-hidden="true" />
          How changes reach the live site
        </h2>
        <ol className="space-y-2.5 text-sm text-gray-700">
          <li className="flex gap-3">
            <Step n={1} />
            <span>
              <strong>Sandbox drafts are private.</strong> Nothing you do in{' '}
              <Link to="/admin/settings/sandbox" className="text-sage-700 hover:underline">
                Design Sandbox
              </Link>{' '}
              is visible to a visitor until you publish it. The preview is yours alone.
            </span>
          </li>
          <li className="flex gap-3">
            <Step n={2} />
            <span>
              <strong>Pages, homepage sections and theme edits are live immediately.</strong> There
              is no draft step on those — a save is a publish. Use the sandbox for anything you
              want to sit with first.
            </span>
          </li>
          <li className="flex gap-3">
            <Step n={3} />
            <span>
              <strong>Everything is undoable.</strong>{' '}
              <Link
                to="/admin/settings/design-history"
                className="text-sage-700 hover:underline"
              >
                Design History
              </Link>{' '}
              keeps the previous state of every change, plus whole-site restore points taken before
              each publish. If something looks wrong, put it back — you don't need to remember what
              it was.
            </span>
          </li>
        </ol>
      </Card>

      <Card padding="lg">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
          <ShieldCheck className="h-5 w-5 text-sage-600" aria-hidden="true" />
          What this account can and can't do
        </h2>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              You can
            </p>
            <ul className="space-y-1.5 text-sm text-gray-700">
              <li>Change colours, fonts and the whole theme</li>
              <li>Edit pages and homepage sections</li>
              <li>Upload, generate and organise images</li>
              <li>Search stock photography and import it</li>
              <li>Restore anything from history</li>
            </ul>
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              You can't — by design, not by accident
            </p>
            <ul className="space-y-1.5 text-sm text-gray-700">
              <li>See orders, customers or payment details</li>
              <li>Change products, prices or inventory</li>
              <li>Touch integrations, credentials or site settings</li>
            </ul>
            {/* Said plainly so a 403 reads as "expected" rather than "broken". */}
            <p className="mt-2 text-xs text-gray-500">
              Those pages aren't hidden because something is misconfigured — the account genuinely
              has no access to them. If you need something from one, ask.
            </p>
          </div>
        </div>
      </Card>

      <Card padding="lg" className="mt-6">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
          <HelpCircle className="h-5 w-5 text-sage-600" aria-hidden="true" />
          Things people ask on day one
        </h2>
        <dl className="space-y-4 text-sm">
          <Faq q="I changed a colour and the storefront looks the same.">
            Check which surface you changed it on. Edits in{' '}
            <Link to="/admin/settings/sandbox" className="text-sage-700 hover:underline">
              Design Sandbox
            </Link>{' '}
            are a private preview until you press Publish to live — that is the whole point of it.
            Edits on the Theme page are live immediately, so if that one looks unchanged, hard-refresh
            the storefront tab.
          </Faq>
          <Faq q="How do I show someone a design before it goes live?">
            Save it as a draft, open <strong>Saved designs</strong>, and press{' '}
            <strong>Share link</strong>. That copies a link showing the design on the real storefront
            to anyone who opens it — no account needed. It lasts 7 days, carries a banner saying it is
            a preview, and you can turn it off at any time.
          </Faq>
          <Faq q="What size should this image be?">
            The{' '}
            <Link to="/admin/settings/design-checklist" className="text-sage-700 hover:underline">
              checklist
            </Link>{' '}
            lists every slot with its exact dimensions and format, and whether it is filled. It also
            copies out as a plain spec sheet you can work from offline.
          </Faq>
          <Faq q="An image I uploaded looks soft or blurry.">
            It was probably smaller than the slot it landed in. Upload at least the size the checklist
            gives — the site scales images down well and up badly.
          </Faq>
          <Faq q="I changed something and it looks wrong. Can I undo it?">
            Yes, always.{' '}
            <Link to="/admin/settings/design-history" className="text-sage-700 hover:underline">
              Design History
            </Link>{' '}
            keeps the previous state of every edit and a whole-site restore point from before each
            publish. You do not need to remember what it used to be.
          </Faq>
          <Faq q="A colour I like is flagged for contrast. Does it matter?">
            It is a warning, not a block — you can publish anyway. It matters most for text and
            buttons, where failing contrast means some visitors genuinely cannot read the label. For a
            decorative panel it is usually fine to proceed.
          </Faq>
        </dl>
      </Card>

      {!designer && (
        <p className="mt-4 text-xs text-gray-500">
          You're signed in as an administrator, so you can see everything. This page is what a
          designer account sees.
        </p>
      )}

      <div className="mt-6 flex flex-wrap gap-4 text-sm">
        <Link to="/admin/pages" className="flex items-center gap-1.5 text-sage-700 hover:underline">
          <FileText className="h-4 w-4" aria-hidden="true" />
          Pages
        </Link>
        <Link to="/admin/homepage" className="flex items-center gap-1.5 text-sage-700 hover:underline">
          <Home className="h-4 w-4" aria-hidden="true" />
          Homepage Builder
        </Link>
        <Link
          to="/admin/settings/design-history"
          className="flex items-center gap-1.5 text-sage-700 hover:underline"
        >
          <History className="h-4 w-4" aria-hidden="true" />
          Design History
        </Link>
      </div>
    </div>
  );
}

function Step({ n }: { n: number }) {
  return (
    <span
      className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sage-100 text-xs font-semibold text-sage-800"
      aria-hidden="true"
    >
      {n}
    </span>
  );
}

function StartCard({
  to, icon: Icon, title, body,
}: {
  to: string;
  icon: typeof Palette;
  title: string;
  body: string;
}) {
  return (
    <Link
      to={to}
      className="group flex flex-col rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition hover:border-sage-300 hover:shadow-md"
    >
      <span className="mb-2 flex items-center gap-2 font-semibold text-gray-900">
        <Icon className="h-5 w-5 text-sage-600" aria-hidden="true" />
        {title}
      </span>
      <span className="flex-1 text-sm text-gray-600">{body}</span>
      <span className="mt-3 flex items-center gap-1 text-sm text-sage-700">
        Open
        <ArrowRight
          className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
          aria-hidden="true"
        />
      </span>
    </Link>
  );
}

/** One question and its answer, in a <dl> so it reads as a reference list. */
function Faq({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="font-medium text-gray-900">{q}</dt>
      <dd className="mt-1 text-gray-700">{children}</dd>
    </div>
  );
}
